// Connection Pooling & Database Connection Management Service
// Netflix/Duolingo-style connection optimization

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

// Get Supabase credentials from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase env vars not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

class ConnectionPoolService {
  // Shared Supabase client (connection pooling handled by Supabase)
  private static sharedClient: SupabaseClient | null = null;
  
  // Connection pool configuration
  private readonly maxConnections = 10;
  private readonly connectionTimeout = 30000; // 30 seconds
  private readonly connectionReuseTime = 60000; // 1 minute

  /**
   * Get optimized Supabase client with connection pooling
   */
  getClient(): SupabaseClient {
    // Use singleton pattern for connection reuse
    if (!ConnectionPoolService.sharedClient) {
      ConnectionPoolService.sharedClient = createClient(supabaseUrl, supabaseKey, {
        db: {
          schema: 'public',
        },
        auth: {
          persistSession: true,
          autoRefreshToken: true,
        },
        global: {
          headers: { 
            'x-client-info': 'theory-coach',
            'x-connection-pool': 'enabled'
          },
        },
        realtime: {
          params: {
            eventsPerSecond: 10
          }
        }
      });
      
      logger.debug('Optimized Supabase client created with connection pooling');
    }

    return ConnectionPoolService.sharedClient;
  }

  /**
   * Execute query with connection pooling
   */
  async executeQuery<T>(
    queryFn: (client: SupabaseClient) => Promise<{ data: T | null; error: any }>
  ): Promise<{ data: T | null; error: any }> {
    const client = this.getClient();
    
    try {
      return await queryFn(client);
    } catch (error) {
      logger.error('Error executing query:', error);
      return { data: null, error };
    }
  }

  /**
   * Execute batch queries (reuses connection)
   */
  async executeBatchQueries<T>(
    queries: Array<(client: SupabaseClient) => Promise<{ data: T | null; error: any }>>
  ): Promise<Array<{ data: T | null; error: any }>> {
    const client = this.getClient();
    const results: Array<{ data: T | null; error: any }> = [];

    // Execute all queries with same connection (connection reuse)
    for (const queryFn of queries) {
      try {
        const result = await queryFn(client);
        results.push(result);
      } catch (error) {
        logger.error('Error in batch query:', error);
        results.push({ data: null, error });
      }
    }

    return results;
  }

  /**
   * Get connection pool statistics
   */
  getPoolStats(): {
    activeConnections: number;
    maxConnections: number;
    connectionUtilization: number;
  } {
    // Supabase handles connection pooling internally
    // This is a placeholder for monitoring
    return {
      activeConnections: 1, // Shared client
      maxConnections: this.maxConnections,
      connectionUtilization: (1 / this.maxConnections) * 100
    };
  }

  /**
   * Reset connection pool (for error recovery)
   */
  resetPool(): void {
    ConnectionPoolService.sharedClient = null;
    logger.debug('Connection pool reset');
  }
}

export const connectionPool = new ConnectionPoolService();



